import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { Duration } from "luxon";

import type { EnumValue } from "@storyboard/dispatch-view/fieldKinds";
import { zinc } from "@/ui/tokens";
import { DISPATCH_COLOR_VALUES } from "./dispatchColors";
import { formatClockAndAge } from "./format";

/** How long the pointer rests on a Dispatch value before its tooltip opens: a timestamp's date, a location's age. */
export const FIELD_VALUE_TOOLTIP_ENTER_DELAY = Duration.fromObject({ milliseconds: 800 });

/**
 * An enum value as a pill: a colored dot and the registry's label, so a duty status reads at a glance and the same
 * value looks the same in every column and view. `paint` overrides the registry color when the view names one.
 */
export function EnumPill({ value, known, paint }: { value: string; known: EnumValue | undefined; paint?: string }) {
  if (!value) return null;
  const color = paint ?? (known?.color ? DISPATCH_COLOR_VALUES[known.color] : zinc[400]);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        height: 20,
        borderRadius: 10,
        bgcolor: "rgba(255, 255, 255, 0.06)",
        fontSize: "0.75rem",
        lineHeight: 1,
        whiteSpace: "nowrap",
        color,
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color, flex: "none" }} />
      {known?.label ?? value}
    </Box>
  );
}

/** A boolean as a mark: a check when true, a muted dash when false, so a column of them scans as a pattern. */
export function BooleanMark({ value, paint }: { value: boolean; paint?: string }) {
  return value ? (
    <CheckIcon aria-label="Yes" sx={{ fontSize: 16, color: paint ?? "var(--color-brand-primary-400)" }} />
  ) : (
    <Box component="span" aria-label="No" sx={{ color: zinc[600] }}>
      —
    </Box>
  );
}

/**
 * A timestamp as the local clock time with its age beside it, the full date on hover: `18:22:23 (42 min ago)`. `now`
 * comes from the page's shared minute clock.
 */
export function RelativeTime({ iso, now }: { iso: string; now: number }) {
  const time = formatClockAndAge(iso, now);
  if (!time) return null;
  return (
    <Tooltip
      title={time.full}
      enterDelay={FIELD_VALUE_TOOLTIP_ENTER_DELAY.toMillis()}
      enterNextDelay={FIELD_VALUE_TOOLTIP_ENTER_DELAY.toMillis()}
    >
      <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        {time.clock}
        {time.age && (
          <Box component="span" sx={{ color: zinc[500], ml: 0.75, fontSize: "0.75rem" }}>
            ({time.age})
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}
